import React, { Suspense, lazy, useEffect, useState } from 'react'
import { useAuth } from './contexts/auth-context'
import { usePermissions } from './contexts/permissions-context'
import { HashRouter, Route, Routes, useLocation, useNavigationType } from 'react-router-dom'
import { Layout } from './common/layout'
import { NotFoundPage } from './common/notFoundPage'
import ErrorBoundary from './common/ErrorBoundary'
import { useUI } from './contexts/UIContext'
import { useFullscreen } from './contexts/screen-context'
import { useBrandFilter } from './contexts/brand-filter-context'
import { NotificationProvider } from './contexts/notification-context'
import DivisionSelector from './pages/divisionSelector/DivisionSelector'
import Loading from './common/Loading'
import RequireAccess from './common/RequireAccess'
import './App.css'
import './utils/messageConfig'

// Route-level code splitting — shrinks the initial bundle and lets each
// page stream in on demand. Suspense fallback below is the branded
// `Loading` component so the hand-off feels seamless.
const HomePage = lazy(() => import('./pages/homePage/HomePage'))
const EditPage = lazy(() => import('./pages/editPage/EditPage'))
const ProjectGallery = lazy(() => import('./pages/projects/ProjectGallery'))
const ProjectDetail = lazy(() => import('./pages/projects/ProjectDetail'))
const AllDesigns = lazy(() => import('./pages/designs/AllDesigns'))
const DesignDeepLink = lazy(() => import('./pages/designs/DesignDeepLink'))
const BatchJobs = lazy(() => import('./pages/batch-jobs/BatchJobs'))
const FavoritesPage = lazy(() => import('./pages/favorites'))
const Studio = lazy(() => import('./pages/studio/Studio'))
const DesignComponent = lazy(() => import('./pages/canvas/Design'))
const KitViewerPage = lazy(() => import('./pages/kitViewer/KitViewerPage'))
const SurveyPage = lazy(() => import('./pages/survey/SurveyPage'))
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'))

function App() {
  const { showSidebar, setShowSidebar } = useUI()
  const { isAuthenticated } = useAuth()
  const { canRead } = usePermissions()
  const { isFullscreen, setIsFullscreen } = useFullscreen()
  const { isBrandSelected: isDivisionSelected } = useBrandFilter()
  const [sidebarExpanded, setSidebarExpanded] = useState(false)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)

  // Update window width when resized
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // Listen for sidebar expansion state changes from the Sidebar component
  useEffect(() => {
    const handleSidebarExpansion = (event) => {
      if (event.detail && Object.prototype.hasOwnProperty.call(event.detail, 'expanded')) {
        setSidebarExpanded(event.detail.expanded)
      }
    }

    window.addEventListener('sidebarExpansionChanged', handleSidebarExpansion)

    return () => {
      window.removeEventListener('sidebarExpansionChanged', handleSidebarExpansion)
    }
  }, [])

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle Escape key for fullscreen exit
      // Let the Canvas component handle sidebar toggling
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false)
        setShowSidebar(true)
      }
    }

    // Add the event listener when the component mounts
    window.addEventListener('keydown', handleKeyDown)

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isFullscreen, setIsFullscreen, setShowSidebar])

  // Responsive margins based on screen size and sidebar state
  const getMarginLeft = () => {
    if (windowWidth <= 768) return '0'
    if (!showSidebar) return '0'
    return sidebarExpanded ? '16rem' : '5rem'
  }

  const mainStyle = {
    marginLeft: getMarginLeft(),
    marginRight: windowWidth <= 768 ? '0' : '.5rem',
    width: windowWidth <= 768 ? '100%' : 'auto',
  }

  // Not for someone without ShortCircuit access: they only get the access-denied page.
  const showDivisionSelector = isAuthenticated && canRead && !isDivisionSelected

  return (
    <ErrorBoundary>
    <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <NotificationProvider>
        {showDivisionSelector && <DivisionSelector />}

        <div className="app-container" style={mainStyle}>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/survey/:surveyId" element={<SurveyPage />} />
            <Route
              path="*"
              element={
                <Layout>
                  <LayoutRoutes isAuthenticated={isAuthenticated} />
                </Layout>
              }
            />
          </Routes>
        </Suspense>
      </div>
      </NotificationProvider>
    </HashRouter>
    </ErrorBoundary>
  )
}

// Inner routes wrapped with a per-pathname key so the route-transition
// fade replays on every navigation. Also scrolls to top on path change so
// new pages start at the top instead of the previous scroll position.
// eslint-disable-next-line react/prop-types -- local component, one prop, used once
function LayoutRoutes({ isAuthenticated }) {
  const location = useLocation()
  const navigationType = useNavigationType()

  useEffect(() => {
    // On back/forward (POP) let the browser restore the prior scroll position.
    // Only force-scroll-top on PUSH or REPLACE.
    if (navigationType === 'POP') return
    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({ top: 0, left: 0, behavior: prefersReduced ? 'auto' : 'smooth' })
  }, [location.pathname, navigationType])

  // Every page needs a session and ShortCircuit access from the user's app role
  // (RequireAccess): 'read' to look, 'write' for the image editor and canvas,
  // which create and change designs, and 'superadmin' for survey admin.
  const guard = (element, level = 'read') =>
    isAuthenticated ? <RequireAccess level={level}>{element}</RequireAccess> : <Loading />

  return (
    <div className="route-transition" key={location.pathname}>
      <Routes>
        <Route path="/" element={guard(<HomePage />)} />
        <Route path="/edit/:projectId" element={guard(<EditPage />, 'write')} />
        <Route path="/canvas" element={guard(<DesignComponent />, 'write')} />
        <Route path="/projects" element={guard(<ProjectGallery />)} />
        <Route path="/projects/:projectId" element={guard(<ProjectDetail />)} />
        <Route path="/designs" element={guard(<AllDesigns />)} />
        <Route path="/batch-jobs" element={guard(<BatchJobs />)} />
        <Route path="/favorites" element={guard(<FavoritesPage />)} />
        {/* Was rendering DesignGallery with a hardcoded placeholder design, so
            every shared link to a design opened an empty gallery. The route
            component fetches the design by id first. */}
        <Route
          path="/projects/:projectId/designs/:designId"
          element={guard(<DesignDeepLink />)}
        />
        {/* The 3D viewer is reading ("View in 3D Studio" from any gallery); its
            Save to Cloud button is write-only inside the page. */}
        <Route path="/viewer" element={guard(<Studio />)} />
        <Route path="/studio" element={guard(<KitViewerPage />)} />
        <Route path="/admin/surveys" element={guard(<AdminDashboard />, 'superadmin')} />
        <Route path="*" element={guard(<NotFoundPage />)} />
      </Routes>
    </div>
  )
}

export default App
